import { useMemo } from 'react';
import { Button, Header, Icon, Label, Segment, Table } from 'semantic-ui-react';
import { describe } from '../approvals.js';
import { buildHome, expectedPeriod } from '../home.js';

const when = new Intl.DateTimeFormat('en-GB', { dateStyle: 'long' });

/* Where every report has got to this month: the latest period each one covers against the period it should cover by now. */
export default function HomeTab({ reports, approvals, onOpen, today = new Date() }) {
  const rows = useMemo(() => buildHome(reports, approvals, today), [reports, approvals, today]);
  const behind = rows.filter((r) => r.state === 'behind').length;
  const unapproved = rows.filter((r) => r.state === 'current' && r.stage !== 'complete').length;
  return (
    <section aria-labelledby="home-heading">
      <Header as="h2" id="home-heading" className="titlebar">
        <Icon name="home" />
        <Header.Content>Home<Header.Subheader>Status of the latest reports as at {when.format(today)}</Header.Subheader></Header.Content>
      </Header>
      <p className="hint">Monthly reports should be reported to <strong>{expectedPeriod('monthly', today).label}</strong>; quarterly reports to <strong>{expectedPeriod('quarterly', today).label}</strong>.</p>
      {!rows.length ? <Segment placeholder><Header icon><Icon name="folder open outline" />There are no reports yet.</Header></Segment> : (
        <>
          <dl className="stats" aria-label="Report status">
            <div><dt>Reports</dt><dd>{rows.length}</dd></div>
            <div className="ok"><dt>Up to date</dt><dd>{rows.length - behind}</dd></div>
            <div className={behind ? 'bad' : 'ok'}><dt>Behind</dt><dd>{behind}</dd></div>
            <div className={unapproved ? 'warn' : 'ok'}><dt>Up to date, not fully approved</dt><dd>{unapproved}</dd></div>
          </dl>
          <div className="gridwrap" tabIndex={0} role="region" aria-label="Latest report status (scrollable)">
            <Table celled compact unstackable className="datagrid hometable">
              <caption className="visually-hidden">The latest report for each report ID, the period due now and its Finance and AM approval status.</caption>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell scope="col" className="lab">Report ID</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Partner</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Account manager</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Frequency</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Period due</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Status</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Finance approval</Table.HeaderCell>
                  <Table.HeaderCell scope="col">AM approval</Table.HeaderCell>
                  <Table.HeaderCell scope="col"><span className="visually-hidden">Open</span></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((r) => (
                  <Table.Row key={`${r.id}/${r.report.report_year}`} className={r.state === 'behind' ? 'bad-differs' : undefined}>
                    <Table.HeaderCell scope="row" className="lab">{r.id}</Table.HeaderCell>
                    <Table.Cell>{r.partner || '–'}</Table.Cell>
                    <Table.Cell>{r.manager || '–'}</Table.Cell>
                    <Table.Cell>{r.frequency === 'monthly' ? 'Monthly' : 'Quarterly'}</Table.Cell>
                    <Table.Cell>{r.expected.label}</Table.Cell>
                    <Table.Cell>
                      {r.state === 'current'
                        ? <Label basic size="small" color="green"><Icon name="check circle" />Up to date</Label>
                        : <Label basic size="small" color="orange" title={`Reported to ${r.reportedTo.label}`}><Icon name="exclamation triangle" />{r.behind} {r.frequency === 'monthly' ? 'month' : 'quarter'}{r.behind === 1 ? '' : 's'} behind</Label>}
                    </Table.Cell>
                    {r.state === 'behind'
                      ? <Table.Cell colSpan={2} className="hint">Not yet reported for {r.expected.label}</Table.Cell>
                      : <>
                        <Table.Cell className="approvalcell">{describe(r.finance) || <span className="hint">Pending</span>}</Table.Cell>
                        <Table.Cell className="approvalcell">{describe(r.account_manager) || <span className="hint">Pending</span>}</Table.Cell>
                      </>}
                    <Table.Cell><Button type="button" basic compact size="small" content="Open" aria-label={`Open ${r.id}`} onClick={() => onOpen(r.report)} /></Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          <p className="hint">Each report is shown at its latest as-at date. Finance and AM approval are for the period due now; a report that has not got that far shows how far it has (hover the status); “Open” goes to the report.</p>
        </>
      )}
    </section>
  );
}
